import { useMemo, useState } from 'react';
import { useApp } from '../store';
import SignOff from './SignOff';
import { can } from '../permissions';
import CutRelease from './CutRelease';
import PullRelease from './PullRelease';
import DevicePicker from './DevicePicker';
import BuildCadence from './BuildCadence';
import { useSampleInert } from './SampleStrip';
import {
  PULLED_LABEL, RELEASE_STATE, deviceLine, deviceList, knownDevices, pulledReading, releaseState,
} from '../releases';
import type { Build } from '../types';
import Info from './Info';

/** The windows the Releases screen can scope to (#3). Default 30 — the widest
 *  common view; the others are one click away and older releases scroll below. */
const RANGES = [7, 14, 30] as const;
type Range = (typeof RANGES)[number];
const DAY_MS = 86_400_000;
const MONTHS_W = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
/** 'YYYY-MM-DD' → '7 Sept 2026' (R16 worded months). */
function wordedDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS_W[m - 1]} ${y}`;
}
/** A ms instant → local 'YYYY-MM-DD'. The window is anchored on the LOCAL calendar
 *  day, like the rest of the app (my-work's dueDaysOf, the masthead's wordedDay).
 *  Slicing toISOString() reads a day ahead every evening west of UTC, so the list
 *  cutoff disagreed with "today" and with the cadence axis. */
function localDay(ms: number): string {
  const t = new Date(ms);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

export default function Releases() {
  const builds = useApp((s) => s.builds);
  const role = useApp((s) => s.shownRole);
  const projects = useApp((s) => s.projects);
  const projectId = useApp((s) => s.projectId);
  // The range the cadence strip and the list are scoped to — Vinnie #3. 30 days
  // on open; older releases sit below the "Earlier releases" divider and scroll.
  const [range, setRange] = useState<Range>(30);
  /*
   * THE SAME RANK THE DATABASE ASKS FOR — `builds_write` wants manager or the
   * board's owner. Offering the button to somebody the policy will refuse is
   * worse than not offering it, because the refusal arrives as nothing
   * happening. Same argument as the sprint screen's buttons.
   */
  const mayRelease = can(role, 'editSettings');
  const inert = useSampleInert();
  const [cutting, setCutting] = useState(false);
  const [pulling, setPulling] = useState('');
  /*
   * A REFUSED WRITE HAS TO REACH THE PERSON. `void putReleaseBack(...)` threw
   * the reason away, so a refusal arrived as nothing happening — which is
   * exactly how Vinnie reported it: "nothing happens when I click put it back".
   * Same fault the release button had, fixed in one place and not the other.
   */
  const [putBackProblem, setPutBackProblem] = useState('');
  const putReleaseBack = useApp((s) => s.putReleaseBack);
  const issues = useApp((s) => s.issues);
  const search = useApp((s) => s.search);
  const openIssue = useApp((s) => s.openIssue);
  // Off unless somebody said otherwise — migration 0024. Off, no card draws a
  // device line at all, which is the whole point: a hosted product has none, and
  // "not recorded" reads as a lapse rather than a question that does not apply.
  const tracksDevices = useApp((s) => s.meta.tracksDevices ?? false);
  const known = useMemo(() => knownDevices(builds), [builds]);
  const signoffs = useApp((s) => s.signoffs);
  /* Asked once per row, from the one place that answers it — see releaseState. */
  const stateOf = (b: Build) =>
    releaseState(b, signoffs.filter((r) => r.build === b.build));

  const byRef = new Map(issues.map((i) => [i.ref, i]));
  // "#187-A" and "#113b" are sub-parts — open the parent item, which is where the work lives.
  // A few old numbers are just another name for a bug that has its own card.
  const ALIASES: Record<string, string> = { '#115': 'B1', '#117': 'B2' };
  const resolve = (ref: string) =>
    byRef.get(ref) ?? byRef.get(ALIASES[ref] ?? '') ?? byRef.get(ref.replace(/^(#\d+).*$/, '$1'));

  const q = search.trim().toLowerCase().replace(/^build\s*/, '');
  const searching = q !== '';
  const visible = searching
    ? builds.filter(
        (b) =>
          String(b.build).includes(q) ||
          b.plain.toLowerCase().includes(q) ||
          b.items.some((r) => r.toLowerCase().includes(q))
      )
    : builds;

  // Scope the list to the chosen window (#3) — but a SEARCH spans every build,
  // so the range + cadence only apply when not searching. A dated build on or
  // after the cutoff is "in range"; everything else (older, or undated) sits
  // under the "Earlier releases" divider and is reached by scrolling — nothing
  // is hidden. Build.date is ISO, so the string compare orders correctly.
  // The window is exactly `range` calendar days ending today — the cutoff is the
  // OLDEST of those days (range-1 back), which is the cadence strip's first column,
  // so the list and the strip cover the identical span (no boundary-day mismatch).
  const cutoffIso = localDay(Date.now() - (range - 1) * DAY_MS);
  const inWindow = (b: Build) => !!b.date && b.date >= cutoffIso;
  const inRange = searching ? visible : visible.filter(inWindow);
  const earlier = searching ? [] : visible.filter((b) => !inWindow(b));
  // The board this screen belongs to — named on the sub-line, the same way the
  // rest of the app resolves the current board.
  const boardName = projects.find((p) => p.id === projectId)?.name ?? 'This board';


  /*
   * AN EMPTY BOARD IS NOW A PLACE TO START, not a dead end. It used to say
   * releases "appear here as releases are shipped" — true only of a board fed
   * by a tool, and every other board read it as a promise nothing was keeping.
   */
  if (builds.length === 0 && !cutting) {
    return (
      // The empty state sits at the READING measure (.cap-read), centred — not the
      // .cap-fill the populated screen uses. A full-bleed empty card with a
      // full-width button reads as broken on a wide monitor (ux/workflow witnesses,
      // 4.0.4); prose and empty-state lines keep the reading cap by doctrine.
      <div className="releases cap-read">
      <div className="empty-col">
        <p>Nothing has been released yet.</p>
        {mayRelease ? (
          <>
            <p className="hint">
              A release is a version that went out, and what went out in it. Record one whenever
              you ship — it does not have to line up with a sprint.
            </p>
            <button data-reveal="cut-release" {...inert('btn btn-primary', () => setCutting(true))}>
              Cut a release
            </button>
          </>
        ) : (
          <p className="hint">They appear here as releases are recorded.</p>
        )}
      </div>
      </div>
    );
  }

  const renderBuild = (b: Build) => (
        <section key={b.id} className={`panel build-card ${b.pulled ? 'is-pulled' : ''}`}>
          <div className="build-head">
            <span className="build-num">Build {b.build}</span>
            {b.version && (
              <Info
                title="App version"
                what={
                  <>
                    The version of the app itself, which changes only when the numbering is
                    deliberately stepped. The <strong>build number</strong> beside it counts every
                    build ever made, so several builds usually share one version.
                  </>
                }
              >
                <span className="build-version">{b.version}</span>
              </Info>
            )}
            {/*
              * WHERE IT HAS GOT TO, on the row rather than only inside it —
              * Vinnie, 21 August: an unsigned release stays on the list and
              * says so, because chasing an approver is the ordinary case and
              * hiding it makes that invisible. A board that signs nothing has
              * every release live from the moment it is recorded, so this reads
              * "Live" throughout and adds no ceremony to anybody's list.
              */}
            {/* A pulled release carries its own "Withdrawn" / "Rolled back"
                badge below; a sign-off state beside it ("Awaiting sign-off")
                contradicts that and reads as still-asking, so it is dropped. */}
            {!b.pulled && (
              <span className={`build-state is-${stateOf(b)}`}>{RELEASE_STATE[stateOf(b)]}</span>
            )}
            {b.date ? (
              b.dateInferred ? (
                <Info
                  className="build-date-slot"
                  title="Approximate date"
                  what="This build's date was not written down at the time. It was worked out afterwards from the surrounding builds, so it is close but not certain."
                >
                  <span className="build-date">about {wordedDate(b.date)}</span>
                </Info>
              ) : (
                <span className="build-date">{wordedDate(b.date)}</span>
              )
            ) : (
              <Info
                className="build-date-slot"
                title="No date recorded"
                what="Nobody wrote down when this build shipped, and there was not enough surrounding information to work it out."
              >
                <span className="build-date muted">date unknown</span>
              </Info>
            )}

            {b.confidence === 'low' && (
              <Info
                title="Approximate record"
                what="This row was pieced together from scattered mentions in the notes rather than written down when the build shipped. What it says it contains is probably right, but it may be missing items or include one that actually shipped elsewhere."
              >
                <span className="build-unsure">approximate</span>
              </Info>
            )}
            {/*
              * THE BADGE SITS IN THE HEAD, beside the version, because somebody
              * scanning this list for "which one is running" reads the heads and
              * nothing else. A withdrawn release that looks ordinary until you
              * read its third line is a withdrawn release somebody installs.
              */}
            {b.pulled && (
              <span className={`build-pulled is-${b.pulled}`}>{PULLED_LABEL[b.pulled]}</span>
            )}
          </div>

          {b.pulled && (
            <div className="pulled-note">
              <strong>{pulledReading(b)}.</strong> {b.pulledWhy}
              {putBackProblem && (
                <span className="pulled-problem" role="alert">
                  {putBackProblem}
                </span>
              )}
              {mayRelease && (
                <button
                  {...inert('btn btn-small pulled-undo', () => {
                    setPutBackProblem('');
                    void putReleaseBack(b.id)
                      .then((wrong) => setPutBackProblem(wrong))
                      .catch((e: { message?: string }) =>
                        setPutBackProblem(e?.message || 'That could not be saved — try again.')
                      );
                  })}
                >
                  Put it back
                </button>
              )}
            </div>
          )}

          <p className="build-plain">{b.plain}</p>

          {b.items.length > 0 && (
            <div className="build-items">
              {b.items.map((ref) => {
                const issue = resolve(ref);
                return issue ? (
                  <button key={ref} className="build-chip linked" onClick={() => openIssue(issue.id)}>
                    {ref} <span className="build-chip-title">{issue.title}</span>
                  </button>
                ) : (
                  <span key={ref} className="build-chip orphan" title="No tracker entry exists for this item">
                    {ref}
                  </span>
                );
              })}
            </div>
          )}

          <SignOff build={b} />

          <Devices build={b} tracksDevices={tracksDevices} mayEdit={mayRelease} known={known} />

          {/*
            * ITS OWN ROW, not tacked onto the end of the phones line. Taking a
            * release back has nothing to do with which handsets it reached, and
            * an action sharing a row with an unrelated sentence is the "last
            * minute thought" placement Vinnie called out on the sprint screen.
            *
            * Offered on the release itself rather than from a menu at the top:
            * WHICH ONE to take back is the whole decision, and picking a version
            * out of a dropdown is how the wrong one gets marked.
            */}
          {/* AFTER the phones line, so the card still reads head → what → who
              got it, and the form is the last thing rather than an interruption
              with an unrelated sentence trailing under it. */}
          {pulling === b.id && (
            <PullRelease build={b} onDone={() => setPulling('')} onCancel={() => setPulling('')} />
          )}

          {mayRelease && !b.pulled && pulling !== b.id && (
            <div className="build-foot">
              <button {...inert('btn btn-small', () => setPulling(b.id))}>
                Take it back
              </button>
            </div>
          )}
        </section>
  );

  return (
    <div className="releases cap-fill">
      {cutting && (
        <CutRelease onDone={() => setCutting(false)} onCancel={() => setCutting(false)} />
      )}

      {/* The screen is titled once — by the topbar, the same as Board, Backlog
          and Documents (and so the empty state above is titled too). This sub-row
          carries the board + count + order on a quiet line, with the date-range
          selector and "Cut a release" on the same baseline to the right. Search
          spans every build, so the range control is hidden while searching. */}
      <div className="rel-head">
        <div className="rel-head-l">
          <p className="rel-sub">
            {boardName} · {builds.length} release{builds.length === 1 ? '' : 's'} · newest first
          </p>
        </div>
        <div className="rel-head-r">
          {!searching && (
            <div className="rel-range">
              <span className="rel-range-lab">Show</span>
              <div className="seg" role="group" aria-label="Date range">
                {RANGES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={range === r}
                    className={`seg-btn ${range === r ? 'active' : ''}`}
                    onClick={() => setRange(r)}
                  >
                    {r} days
                  </button>
                ))}
              </div>
            </div>
          )}
          {mayRelease && !cutting && (
            <button data-reveal="cut-release" {...inert('btn btn-primary', () => setCutting(true))}>
              Cut a release
            </button>
          )}
        </div>
      </div>
      <div className="fd-rule" />

      {/* THE SHIPPING CADENCE, over the window it counts — moved here from Insights
          (#303 follow-up). Fixed-footprint bars so it never rescales (#3). */}
      {!searching && <BuildCadence days={range} />}

      {!searching && inRange.length === 0 && earlier.length > 0 && (
        <p className="rel-none">Nothing released in the last {range} days — earlier releases are below.</p>
      )}

      {inRange.map(renderBuild)}

      {!searching && earlier.length > 0 && (
        <div className="rel-divider">
          <span className="rel-divider-h">Earlier releases</span>
          <span className="rel-divider-note">
            {earlier.length} release{earlier.length === 1 ? '' : 's'} before {wordedDate(cutoffIso)} · scroll to see them
          </span>
        </div>
      )}

      {earlier.map(renderBuild)}

      {visible.length === 0 && <div className="empty-col">No builds match “{search}”.</div>}
    </div>
  );
}

/**
 * WHAT A RELEASE WENT ONTO — in three states, where there used to be two.
 *
 * The missing one was the important one. Every release on every board that does
 * not ship to devices said *"Phones not recorded"* for ever, which is an
 * accusation of sloppiness about a fact that does not exist. Vinnie read it on
 * his own board and asked the right question: *"if I am developing a SaaS then
 * there will be no recorded device."*
 *
 *   off     the project does not ship to devices — no line at all
 *   empty   it does, and this release has none written down yet
 *   some    the list, with the ones a tool reported marked as such
 *
 * WHY THE TOOL'S ONES ARE MARKED. The list has two authors who know different
 * things, and when it disagrees with the world the first question is which half
 * to distrust. See mergeDevices in src/releases.ts for the ownership rule.
 */
function Devices({
  build,
  tracksDevices,
  mayEdit,
  known,
}: {
  build: Build;
  tracksDevices: boolean;
  mayEdit: boolean;
  known: string[];
}) {
  const setReleaseDevices = useApp((s) => s.setReleaseDevices);
  const inert = useSampleInert();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  const line = deviceLine(build, tracksDevices);
  // Not a hidden line — no line. A board with no devices has nothing to say
  // here, and saying nothing is the correct amount.
  if (line.kind === 'off') return null;

  const start = () => {
    setProblem('');
    setDraft(deviceList(build.installed));
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    let wrong = '';
    try {
      wrong = await setReleaseDevices(build.id, draft);
    } catch (e) {
      wrong = (e as { message?: string })?.message || 'That could not be saved — try again.';
    } finally {
      // Cleared whatever happens. A refused write that leaves a button reading
      // "Saving…" for ever is how "put it back does nothing" was reported.
      setBusy(false);
    }
    setProblem(wrong);
    if (!wrong) setEditing(false);
  };

  if (editing) {
    return (
      <div className="build-devices is-editing">
        <span className="build-devices-label">Installed on</span>
        <DevicePicker devices={draft} offered={known} onChange={setDraft} />
        {problem && (
          <span className="pulled-problem" role="alert">
            {problem}
          </span>
        )}
        <div className="cut-device-add">
          <button disabled={busy} {...inert('btn btn-small btn-primary', () => void save())}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-small" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="build-devices">
      {line.kind === 'some' ? (
        <>
          {/* A MARKED PILL NEEDS SOMEWHERE THAT SAYS WHAT THE MARK MEANS. A dot
              with only a tooltip behind it is information carried by appearance
              alone, which is the same fault as a status shown only in colour —
              and this one decides which half of the list to believe. The Info
              dot is how the rest of this screen explains itself. */}
          {line.devices.some((d) => d.told) ? (
            <Info
              title="Where these came from"
              what={
                <>
                  A marked device was <strong>reported by the tool</strong> that pushes to this
                  board — it was installed at the moment of the push. An unmarked one was{' '}
                  <strong>added here by a person</strong>, usually afterwards: a cable install the
                  next day, or an update that arrived overnight. The tool restates its own on every
                  push and never touches the rest.
                </>
              }
            >
              <span className="build-devices-label">Installed on</span>
            </Info>
          ) : (
            <span className="build-devices-label">Installed on</span>
          )}
          {line.devices.map((d) => (
            <span
              key={d.name}
              className={`device-pill ${d.told ? 'is-told' : ''}`}
              title={
                d.told
                  ? 'Reported by the tool that pushes to this board'
                  : 'Added here by a person'
              }
            >
              {d.name}
            </span>
          ))}
        </>
      ) : (
        <Info
          title="No devices recorded"
          what="Nobody noted what this release was installed on. It does not mean it went nowhere — only that it was not written down. A release usually reaches its devices over the following days, so this can be filled in afterwards."
        >
          <span className="build-devices-label muted">Devices not recorded</span>
        </Info>
      )}
      {mayEdit && (
        <button {...inert('btn btn-small build-devices-edit', start)}>
          {line.kind === 'some' ? 'Edit' : 'Add devices'}
        </button>
      )}
    </div>
  );
}
